import { merge } from 'lodash-es';
import { defineStore, storeToRefs } from 'pinia';
import { ContextType } from '@/router/routes';
import { useUserStore } from '@/store/userStore';
import { EntitlementAction, EntitlementType, useEntitlementsStore } from '@/store/entitlementsStore';
import getEnv from '@/utils/env';
import { NaturalPersonURI } from '@/types/naturalPerson';
import { useProfileStore } from '@/store/profileStore';
import { isPartnershipEngagement, PartnershipType, usePartnershipsStore } from '@/store/partnershipsStore';
import { PartnershipStatus, Role } from '@/types/partnership';

export interface ContextSwitch {
  uid: string;
  name: string;
  pictureUri?: string;
  registeryUid?: string;
  legalForm?: string;
  nickname?: string;
  addressLine1?: string;
  addressLine2?: string;
  phone?: string;
  email?: string;
}
interface ContextState {
  currentContextUid?: string;
  contexts: { [key: string]: ContextSwitch };
}

export const useContextSwitchStore = defineStore('contextSwitch', {
  state: (): ContextState => ({
    currentContextUid: undefined,
    contexts: {}
  }),
  getters: {
    isPrivateContext: (state) => {
      const userStore = useUserStore();
      return !state.currentContextUid || state.currentContextUid === userStore.uid;
    },
    isRegisteredContext: (state) => {
      const entitlementsStore = useEntitlementsStore();
      const userStore = useUserStore();
      if (state.currentContextUid) {
        return (
          userStore.isRegistered &&
          entitlementsStore.isEntitledToType(
            state.currentContextUid,
            EntitlementAction.View,
            EntitlementType.PartnershipContract
          ) &&
          !entitlementsStore.isEntitledToType(
            state.currentContextUid,
            EntitlementAction.Capture,
            EntitlementType.Partnership
          ) &&
          !entitlementsStore.hasCaptureEntitlementToAnyAccount &&
          !entitlementsStore.hasCaptureAndApproveEntitlementToAnyAccount
        );
      }
    },
    isInternalRegistryPartnership: (state) => {
      const validForms = ['simple_partnership', 'association', 'body_corporate'];
      const context = state.currentContextUid ? state.contexts[state.currentContextUid] : undefined;
      return !!context?.legalForm && validForms.includes(context?.legalForm);
    },
    getContextType(state) {
      if (this.isPrivateContext) {
        return ContextType.Private;
      } else if (this.isRegisteredContext) {
        return ContextType.Registered;
      } else {
        return ContextType.Partnership;
      }
    },
    getContexts: (state) => {
      return Object.values(state.contexts);
    },
    getPrivateContext(): ContextSwitch {
      const userStore = useUserStore();
      const { communication } = storeToRefs(useProfileStore());
      return {
        uid: userStore.uid,
        name: `${userStore.name} ${userStore.familyName}`,
        pictureUri: !!communication.value.myPicture
          ? `${getEnv('VITE_APP_GW_HOST')}/api${NaturalPersonURI.Base}/${userStore.uid}${
              NaturalPersonURI.MyPicture
            }?apiKey=${getEnv('VITE_APP_API_KEY')}`
          : undefined,
        nickname: communication.value.nickName,
        addressLine1: `${userStore.address?.agentAddress.street} ${userStore.address?.agentAddress.number}`,
        addressLine2: `${userStore.address?.agentAddress.postalCode} ${userStore.address?.agentAddress.city} (${userStore.address?.agentAddress.country})`,
        phone: `${userStore.phone?.agentPhoneNumber.countryCode} ${userStore.phone?.agentPhoneNumber.number}`,
        email: userStore.email
      };
    }
  },
  actions: {
    getCurrentContext() {
      let context;
      /**
       * If the local state does not contain the currentContextUid, check if we previously saved it into localStorage.
       * This allows to keep the context upon page reload.
       */
      if (!this.currentContextUid) {
        if (typeof localStorage !== 'undefined') {
          const ctxFromStorage = localStorage.getItem('currentContextUid');
          if (ctxFromStorage) {
            if (!this.contexts[ctxFromStorage]) {
              /**
               * In case the contexts haven't finish loading yet or if the context saved in localStorage was for a context from a
               * different user that is not available to the current user.
               * The 2nd case should not happen when the user perform a logout but could happen with multiple window open.
               */
              return undefined;
            }
            this.setCurrentContext(ctxFromStorage);
          }
        }
      }
      if (this.currentContextUid) {
        context = this.contexts[this.currentContextUid];
      }
      return context;
    },
    loadPartnershipContexts() {
      const partnershipsStore = usePartnershipsStore();

      partnershipsStore.getPartnerships
        .filter((part: PartnershipType) => {
          const isIdentified = part.status === PartnershipStatus.Identified;
          const isScreened = part.status === PartnershipStatus.Screened;
          const isBankable = part.status === PartnershipStatus.Bankable;
          const hasBootstrapperOnly =
            part.partnerEngagements[0].roles.includes(Role.Bootstrapper) &&
            part.partnerEngagements[0].roles.length === 1;
          return isIdentified || isScreened || (isBankable && !hasBootstrapperOnly);
        })
        .forEach((part: PartnershipType) => {
          if (isPartnershipEngagement(part)) {
            this.addContext({
              uid: part.uid,
              name: part.name,
              pictureUri: '',
              legalForm: part.legalForm
            });
          } else {
            this.addContext({
              uid: part.uid,
              name: part.name.name,
              pictureUri: '',
              legalForm: part.entity.legalForm,
              addressLine1: part.contactDetails
                ? `${part.contactDetails?.domicileAddress.street} ${part.contactDetails?.domicileAddress.number}`
                : '',
              addressLine2: part.contactDetails
                ? `${part.contactDetails?.domicileAddress.postalCode} ${part.contactDetails?.domicileAddress.city} (${part.contactDetails.domicileAddress.country})`
                : '',
              registeryUid: part.externalIds?.filter((i) => i.scheme === 'uid_reg')[0]?.value
            });
          }
        });
    },
    setCurrentContext(uid: string) {
      this.currentContextUid = uid;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('currentContextUid', uid);
      }
    },
    setUserContext(context: ContextSwitch) {
      this.contexts[context.uid] = context;
    },
    addContext(context: ContextSwitch) {
      this.contexts[context.uid] = context;
    },
    updateContext(ctxUid: string, updated: ContextSwitch) {
      this.contexts[ctxUid] = merge(this.contexts[ctxUid], updated);
    }
  }
});
